import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, ShoppingCart, PackageCheck, KeyRound, Mail, MessageCircle, AlertTriangle, BellRing } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDateTime } from "@/lib/status";

const REFRESH_MS = 30_000;

// Per-type icon + colour so mixed notifications are easy to tell apart at a glance.
function notifVisual(n) {
  switch (n.type) {
    case "purchasing_assigned": return { Icon: ShoppingCart, cls: "bg-amber-100 text-amber-700" };
    case "message": return { Icon: MessageCircle, cls: "bg-indigo-100 text-indigo-700" };
    case "discrepancy": return { Icon: AlertTriangle, cls: "bg-rose-100 text-rose-700" };
    case "reminder": return { Icon: BellRing, cls: "bg-amber-100 text-amber-700" };
    default:
      if (n.channel === "whatsapp") return { Icon: MessageCircle, cls: "bg-emerald-100 text-emerald-700" };
      return { Icon: Mail, cls: "bg-blue-100 text-blue-700" };
  }
}

// Short two-tone "ding" via Web Audio (no asset needed). Created lazily after a
// user gesture so browsers don't block it.
let _audioCtx = null;
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_audioCtx) _audioCtx = new Ctx();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + i * 0.12;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  } catch (_) { /* audio unsupported */ }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ purchasing: 0, warehouse: 0, password_resets: 0, notifications: 0 });
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const seenIdsRef = useRef(null); // null until first load completes

  // Pop a desktop notification + ding for genuinely new, unread items.
  const maybeNotify = (list) => {
    if (seenIdsRef.current === null) {
      // First load: remember what's already here, don't pop retroactively.
      seenIdsRef.current = new Set(list.map((n) => n.id));
      return;
    }
    const fresh = list.filter((n) => !seenIdsRef.current.has(n.id) && !n.read);
    list.forEach((n) => seenIdsRef.current.add(n.id));
    if (fresh.length === 0) return;
    if ("Notification" in window && Notification.permission === "granted") {
      fresh.slice(0, 3).forEach((n) => {
        try {
          const notif = new Notification("SatınAlTrack", {
            body: n.message,
            icon: "/icon-192.png",
            tag: n.id,
          });
          notif.onclick = () => {
            window.focus();
            if (n.request_id) navigate(`/talepler/${n.request_id}`);
            notif.close();
          };
        } catch (_) { /* notification blocked */ }
      });
    }
    playDing();
  };

  const fetchAll = async () => {
    try {
      const [c, n] = await Promise.all([
        api.get("/inbox/counts"),
        api.get("/notifications"),
      ]);
      setCounts(c.data);
      setNotifs(n.data);
      maybeNotify(n.data);
    } catch (_) { /* offline */ }
  };

  useEffect(() => {
    if (!user || !user.id) return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    seenIdsRef.current = null; // reset baseline on user change
    fetchAll();
    const id = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const showPurchasing = isAdmin || user.role === "purchasing";
  const showWarehouse = true;
  const showResets = isAdmin;

  const unreadNotifs = notifs.filter((n) => !n.read).length;
  const total =
    unreadNotifs +
    (showPurchasing ? counts.purchasing : 0) +
    (showWarehouse ? counts.warehouse : 0) +
    (showResets ? counts.password_resets : 0);

  const onOpenChange = async (next) => {
    setOpen(next);
    if (next && unreadNotifs > 0) {
      try {
        await api.post("/notifications/mark-read");
        setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
        setCounts((c) => ({ ...c, notifications: 0 }));
      } catch (_) { /* ignore */ }
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100"
          aria-label="Bildirimler"
        >
          <Bell className="h-5 w-5 text-slate-700" />
          {total > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {total > 99 ? "99+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2 max-h-[70vh] overflow-y-auto thin-scroll" data-testid="notification-panel">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600 px-2 py-1.5">
          Bildirimler
        </div>

        {/* Personal notification feed */}
        {notifs.length > 0 && (
          <div className="space-y-1 mb-1">
            {notifs.slice(0, 10).map((n) => {
              const { Icon, cls } = notifVisual(n);
              return (
              <Link
                key={n.id}
                to={`/talepler/${n.request_id}`}
                data-testid={`notif-item-${n.id}`}
                onClick={() => setOpen(false)}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-md hover:bg-slate-100 ${n.read ? "" : "bg-blue-50/60"}`}
              >
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${cls}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 leading-snug">{n.message}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{formatDateTime(n.created_at)}</div>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />}
              </Link>
              );
            })}
          </div>
        )}

        {/* Role-based shortcuts */}
        {(showPurchasing || showWarehouse || showResets) && (
          <div className="space-y-1 border-t border-slate-100 pt-1">
            {showPurchasing && (
              <Link
                to="/satinalma-kutusu"
                data-testid="notif-link-purchasing"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md hover:bg-slate-100"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <ShoppingCart className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">Satın Alma Kutusu</div>
                    <div className="text-xs text-slate-500">İletilen talepler</div>
                  </div>
                </div>
                <span className={`text-xs font-bold ${counts.purchasing > 0 ? "text-amber-700 bg-amber-100" : "text-slate-400 bg-slate-100"} rounded-full px-2 py-0.5`}>
                  {counts.purchasing}
                </span>
              </Link>
            )}
            {showWarehouse && (
              <Link
                to="/depo-kutusu"
                data-testid="notif-link-warehouse"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md hover:bg-slate-100"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <PackageCheck className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">Depo Kontrolü</div>
                    <div className="text-xs text-slate-500">Kontrol bekleyenler</div>
                  </div>
                </div>
                <span className={`text-xs font-bold ${counts.warehouse > 0 ? "text-emerald-700 bg-emerald-100" : "text-slate-400 bg-slate-100"} rounded-full px-2 py-0.5`}>
                  {counts.warehouse}
                </span>
              </Link>
            )}
            {showResets && (
              <Link
                to="/kullanicilar"
                data-testid="notif-link-resets"
                className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md hover:bg-slate-100"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">Şifre Talepleri</div>
                    <div className="text-xs text-slate-500">Yeni şifre üretin</div>
                  </div>
                </div>
                <span className={`text-xs font-bold ${counts.password_resets > 0 ? "text-rose-700 bg-rose-100" : "text-slate-400 bg-slate-100"} rounded-full px-2 py-0.5`}>
                  {counts.password_resets}
                </span>
              </Link>
            )}
          </div>
        )}

        {notifs.length === 0 && !showPurchasing && !showWarehouse && !showResets && (
          <div data-testid="notif-empty" className="text-center text-sm text-slate-400 py-6">
            Henüz bildirim yok
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
